import React, { useEffect } from 'react';
import SubmarketInfoText from '../SubmarketInfoText';
import { Chip, Grid, Typography, Box, Button, Skeleton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SubmarketStaticMap from './SubmarketStaticMap';
import SubmarketCard from './SubmarketCard';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { MARKET_WIDE_BADGE_TOOLTIP } from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/overviewSectionBadgeTooltips';
import { SUBMARKET_SORTING_OPTIONS } from 'src/utils/const';
import { checkIfMarketTrackedAlready, getTop3Submarkets } from './helper';
import TopSubmarketGraph from './TopSubmarketGraph';
import { styles } from './styles';
import { logAmplitudeEvent } from 'src/utils/analytics';
import RateLimitAlert from '../Alerts/RateLimitAlert';

export interface SubmarketCTAProps {
  parentMarketID: number;
  currentMarketID: number;
  marketDisplayName: string;
  isMobileView: boolean;
  handleOnSampleClick: (plan: string) => void;
  submarkets;
  setDashboardLayoutView: (view: string) => void;
  isSubmarketAccessAllowed: boolean;
  submarketSummary: any;
  parentMarketDetails: any;
  submarketSortKey: string;
  isSubmarket: boolean;
  submarketParentMarketPerformance: any;
  source?: 'overview' | 'calculator';
  handleCtaClick?: () => void;
  hideMap?: boolean;
  customHeaderText?: React.ReactNode;
  formattedSubmarketSummary?: any;
  top3SubmarketsRef?: any;
  topSubMarketChartRef?: any;
  isSubmarketRateLimited: boolean;
  filterApplied?: boolean;
}

const SubmarketCTA = ({
  parentMarketID,
  currentMarketID,
  marketDisplayName,
  isMobileView,
  handleOnSampleClick,
  submarkets,
  setDashboardLayoutView,
  isSubmarketAccessAllowed,
  submarketSummary,
  parentMarketDetails,
  submarketSortKey,
  isSubmarket,
  submarketParentMarketPerformance,
  source,
  handleCtaClick,
  hideMap,
  customHeaderText,
  formattedSubmarketSummary,
  top3SubmarketsRef,
  topSubMarketChartRef,
  isSubmarketRateLimited,
  filterApplied = false,
}: SubmarketCTAProps) => {
  useEffect(() => {
    if (
      !submarkets ||
      submarkets === 'loading' ||
      !Array.isArray(submarkets) ||
      submarkets.length === 0
    ) {
      return;
    }

    if (checkIfMarketTrackedAlready('submarket_cta_viewed', currentMarketID)) return;

    logAmplitudeEvent('submarket_cta_viewed', {
      market_name: marketDisplayName,
      is_submarket: isSubmarket,
      market_id: currentMarketID,
      parent_market_id: parentMarketID,
      submarkets_count: submarkets?.length,
    });
  }, [submarkets]);

  if (submarkets === 'loading') {
    return (
      <Box p={1} sx={{ width: '100%' }}>
        {/* Full width skeleton row */}
        <Skeleton variant="rectangular" height={100} sx={{ mb: 1, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={100} sx={{ mb: 1, borderRadius: 2 }} />
        {/* Two equal-width skeleton boxes side-by-side */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (isSubmarketRateLimited) {
    return (
      <Box p={1} sx={{ width: '100%' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>Top Submarkets in {marketDisplayName}</Typography>
        <RateLimitAlert />
      </Box>
    );
  }

  if (submarkets.length === 0) {
    return <></>;
  }

  const TopSubmarketGraphComponent =
    source !== 'calculator' ? (
      <TopSubmarketGraph
        submarkets={submarkets}
        marketDisplayName={marketDisplayName}
        handleOnSampleClick={handleOnSampleClick}
        isSubmarketAccessAllowed={isSubmarketAccessAllowed}
        setDashboardLayoutView={setDashboardLayoutView}
        parentMarketDetails={parentMarketDetails}
        submarketParentMarketPerformance={submarketParentMarketPerformance}
        isMobileView={isMobileView}
        top3SubmarketsRef={top3SubmarketsRef}
        topSubMarketChartRef={topSubMarketChartRef}
      />
    ) : null;  

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="h1"
          className="marketTitle subsection--spacing"
          id="location-tab"
        >
          {customHeaderText ? (
            customHeaderText
          ) : (
            <>
              Where {isSubmarket && 'else'} should I look in {marketDisplayName}?
            </>
          )}
        </Typography>
        {filterApplied && (
          <Tooltip title={MARKET_WIDE_BADGE_TOOLTIP} arrow placement="top">
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.375,
                px: 1.25,
                py: 0.375,
                borderRadius: 10,
                backgroundColor: COLOR_PRIMARY_2,
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'help',
                alignSelf: 'flex-start',
                mt: -0.5,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '0.875rem' }} />
              Filter not applied
            </Box>
          </Tooltip>
        )}
      </Box>
      <SubmarketInfoText
        submarketSummary={submarketSummary}
        isMobileView={false}
        handleOnSampleClick={handleOnSampleClick}
        sx={{ mb: 2 }}
        TopSubmarketGraphComponent={TopSubmarketGraphComponent}
        formattedSubmarketSummary={formattedSubmarketSummary}
      />

      <Grid container spacing={2} justifyContent="space-between" sx={{ width: '100%' }}>
        {/* Map Section */}
        {!hideMap && (
          <Grid item xs={12} md={source === 'calculator' ? 12 : 6}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Top {submarkets.length > 1 ? 'Submarkets' : 'Submarket'} in{' '}
              <span style={{ fontWeight: 600 }}>
                {parentMarketDetails &&
                  (parentMarketDetails?.displayName || parentMarketDetails?.district)}
                {!parentMarketDetails && marketDisplayName}
              </span>
            </Typography>
            <SubmarketStaticMap
              submarkets={submarkets}
              handleOnSampleClick={handleOnSampleClick}
              isSubmarketAccessAllowed={isSubmarketAccessAllowed}
              setDashboardLayoutView={setDashboardLayoutView}
              isMobileView={isMobileView}
            />
          </Grid>
        )}

        {/* Card Section */}
        <Grid container item xs={12} md={source === 'calculator' ? 12 : 6}>
          <Grid container item xs={12} justifyContent="flex-end" sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`Sorted by ${SUBMARKET_SORTING_OPTIONS[submarketSortKey].name}`}
              color="default"
              variant="outlined"
              clickable={true}
              onClick={() => setDashboardLayoutView('submarkets')}
            />
          </Grid>

          <Grid item xs={12}>
            {submarkets &&
              Array.isArray(submarkets) &&
              getTop3Submarkets(submarkets).map((submarket, index) => (
                <SubmarketCard
                  key={index + submarket.submarket_name}
                  handleOnSampleClick={handleOnSampleClick}
                  submarketData={submarket}
                  isSubmarketAccessAllowed={isSubmarketAccessAllowed}
                />
              ))}
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <CTAToSubmarketPage
              handleOnSampleClick={handleOnSampleClick}
              submarketsLength={submarkets?.length || 0}
              setDashboardLayoutView={setDashboardLayoutView}
              isSubmarketAccessAllowed={isSubmarketAccessAllowed}
              handleCtaClick={handleCtaClick}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

const CTAToSubmarketPage = ({
  handleOnSampleClick,
  submarketsLength,
  setDashboardLayoutView,
  isSubmarketAccessAllowed,
  handleCtaClick,
}: {
  handleOnSampleClick?: (plan: string) => void;
  submarketsLength: number;
  setDashboardLayoutView: (view: string) => void;
  isSubmarketAccessAllowed: boolean;
  handleCtaClick?: () => void;
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '1px solid #e9ecef',
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '14px',
            color: '#6c757d',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          Compare neighborhood grade & performance data for all{' '}
          {submarketsLength ? submarketsLength : ''}+ submarkets
        </Typography>
      </Box>

      <Button
        variant="contained"
        onClick={() => (handleCtaClick ? handleCtaClick() : setDashboardLayoutView('submarkets'))}
        sx={styles.ctaButton}
      >
        Compare Submarkets
      </Button>
    </Box>
  );
};

export default SubmarketCTA;
